import React, { useState } from 'react';
import { useRegisterUser } from '../../api/users';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import { useLoginModalContext } from '../../context/LoginModalContext';

interface RegisterFormProps {
  onRegister: (email: string) => void;
  onClose: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onRegister, onClose }) => {
  const [name, setName] = useState('');
  const { email, setEmail } = useLoginModalContext();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const registerUserMutation = useRegisterUser();
  const { register, signInWithGoogle } = useUserContext();
  const { showError } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && email.trim() && agreeToTerms) {
      try {
        const result = await registerUserMutation.mutateAsync({ name: name.trim(), email: email.trim() });

        register(result.user);
        onRegister(result.user.email);
      } catch (error: any) {
        if (error.status === 409) {
          showError("An account with this email already exists. Please log in instead.", error);
        } else {
          showError("Error registering the user, please try again.", error);
        }
      
        
      }
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      onClose();
    } catch (error: any) {
      console.error('Google login error:', error);
      showError('Failed to sign in with Google. Please try again.', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Seeker"
            required
            disabled={registerUserMutation.isPending}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@example.com"
            required
            disabled={registerUserMutation.isPending}
          />
        </div>
        
        <div className="terms-checkbox">
          <input
            type="checkbox"
            id="terms"
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            disabled={registerUserMutation.isPending}
          />
          <label htmlFor="terms" className="terms-label">
            I agree to the <a 
              href="/terms" 
              onClick={(e) => {
                e.preventDefault();
                window.open('/terms', 'terms', 'width=800,height=600,scrollbars=yes,resizable=yes');
              }}
              className="terms-link"
            >
              terms & conditions
            </a>.
          </label>
        </div>
        
        <button 
          type="submit" 
          className="btn btn--primary btn--register"
          disabled={registerUserMutation.isPending || !agreeToTerms || !name.trim() || !email.trim()}
        >
          {registerUserMutation.isPending ? 'Registering...' : 'Register Now'}
        </button>
      </form>

      <div className="login-divider">
        <span>or</span>
      </div>

      <button 
        type="button" 
        className="btn btn--google"
        onClick={handleGoogleLogin}
        disabled={isGoogleLoading}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
      </button>
    </>
  );
};

export default RegisterForm;
